<template>
    <div
        class="panel is-success pos-rel">
        <p
            class="panel-heading is-flex is-justify-content-space-between is-align-items-center">
            PIMS DC (Document Control, Interface)
            <a @click="onClickShowDocumentProjects">Show Plant Project Codes (DocumentProjects)</a>
            <a
                v-require-can-edit-code="{ libraryName: 'ApplicationDocumentManagement' }"
                class="button is-pulled-right is-small"
                @click="$emit('editAdm', applicationDocumentManagementViewModel)">
                <b-icon
                    :icon="iconDisplayed"
                    size="is-small" />
            </a>
        </p>
        <div class="panel-block">
            <b-table
                class="headless-table"
                :data="applicationDocumentManagementViewModel.properties"
                default-sort="name"
                :striped="false"
                :narrowed="true"
                :hoverable="false">
                <b-table-column
                    v-slot="props"
                    field="name"
                    label="Name">
                    {{ props.row.name }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="value"
                    label="Value">
                    {{ props.row.value }}
                </b-table-column>
                <template #empty>
                    <div class="has-text-centered">
                        {{ errorMessage }}
                    </div>
                </template>
            </b-table>
            <b-loading
                v-model="loadingApplicationDocumentManagement"
                :is-full-page="false"
                :can-cancel="false" />
        </div>
        <template
            v-if="isOpenDocumentProjectModal">
            <modal-base @onCancel="onClose">
                <template
                    #modal-header>
                    Document Projects
                    <button
                        class="delete is-pulled-right"
                        aria-label="close"
                        @click="onClose" />
                </template>
                <template
                    #modal-body>
                    <b-table
                        :loading="loadingDocumentProjects"
                        :data="documentProjects"
                        default-sort="Name"
                        striped
                        narrowed>
                        <b-table-column
                            v-slot="props"
                            field="Name"
                            label="Name"
                            sortable
                            searchable>
                            {{ props.row.Name }}
                        </b-table-column>
                        <b-table-column
                            v-slot="props"
                            field="Description"
                            label="Description"
                            searchable
                            sortable>
                            {{ props.row.Description }}
                        </b-table-column>
                        <template #empty>
                            <div class="has-text-centered">
                                {{ errorMessage }}
                            </div>
                        </template>
                    </b-table>
                </template>
            </modal-base>
        </template>
    </div>
</template>

<script>
    import { sqlQuery } from '@/shared/helpers/api';
    import { getLibraryLink } from '@/shared/helpers/routing';
    import { encodeIdBase64 } from '@/shared/helpers/utils';
    import { http } from '@/shared/httpWrapper.js';
    import { showMixin } from '@/shared/mixins/showMixin.js';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import { requireCanEditCode } from '@/shared/directives/requirePermission';

    export default {
        directives: {
            'require-can-edit-code': requireCanEditCode
        },
        components: { ModalBase },
        mixins: [
            showMixin
        ],
        props: {
            projectMaster: {
                type: String,
                required: true
            }
        },
        emits: [
            'editAdm'
        ],
        data() {
            return {
                applicationDocumentManagement: [],
                loadingApplicationDocumentManagement: false,
                loadingDocumentProjects: false,
                isOpenDocumentProjectModal: false,
                documentProjects: [],
                errorMessage: ''
            };
        },
        computed: {
            applicationDocumentManagementViewModel() {
                if (this.applicationDocumentManagement.length > 0) {
                    if (this.applicationDocumentManagement.length > 1) {
                        return {
                            properties: [
                                {
                                    name: 'Error',
                                    value: `Found multiple IDMs linked (${this.applicationDocumentManagement.map(adm => adm.Name).join(', ')})`
                                }
                            ] };
                    }

                    const linked = this.applicationDocumentManagement[0];

                    return {
                        id: encodeIdBase64('Code', linked.Id),
                        properties: [
                            { name: 'Name', value: linked.Name },
                            { name: 'CommonLib description', value: linked.Description },
                            { name: 'PIMS DC internal domain', value: linked.PIMSDCInternal },
                            { name: 'PIMS DC internal description', value: linked.InternalDescription },
                            { name: 'PIMS DC internal is archived', value: linked.InternalIsArchived },
                            { name: 'PIMS DC technical domain', value: linked.PIMSDCTechnical },
                            { name: 'PIMS DC technical description', value: linked.TechnicalDescription },
                            { name: 'PIMS DC technical is archived', value: linked.TechnicalIsArchived },
                            { name: 'Comment', value: linked.Comment },
                            { name: 'Internal comment', value: linked.InternalComment }
                        ]
                    };
                }

                return {
                    properties: []
                };
            },
            iconDisplayed() {
                const admvm = this.applicationDocumentManagementViewModel;
                const showAddSymbol = admvm.properties.length === 0;
                return showAddSymbol ? 'plus-thick' : 'pencil';
            }
        },
        async mounted() {
            this.loadingApplicationDocumentManagement = true;
            this.applicationDocumentManagement = await this.getApplicationDocumentManagement();
            this.loadingApplicationDocumentManagement = false;
        },
        methods: {
            async getApplicationDocumentManagement() {
                try {
                    const query = ` SELECT
                                        a.Id,
                                        a.Name,
                                        a.Description,
                                        a.PIMSDCInternal,
                                        a.PIMSDCTechnical,
                                        a.Comment,
                                        a.InternalComment,
                                        internal.Description AS InternalDescription,
                                        technical.Description AS TechnicalDescription,
                                        internal.IsArchived AS InternalIsArchived,
                                        technical.IsArchived AS TechnicalIsArchived
                                    FROM [ApplicationDocumentManagement] as a
                                    LEFT JOIN ApplicationPIMSDC AS internal
                                        ON internal.Name = a.PIMSDCInternal
                                    LEFT JOIN ApplicationPIMSDC AS technical
                                        ON technical.Name = a.PIMSDCTechnical
                                    WHERE a.IsValid = 1 and a.ProjectMaster = @projectMaster`;
                    const res = await sqlQuery(http, {
                        query,
                        arguments: { projectMaster: this.projectMaster } });
                    return res.data;
                } catch {
                    const errorMessage = 'Failed to fetch data for IDM';
                    this.showError(errorMessage);
                    this.errorMessage = errorMessage;
                    return { data: [] };
                }
            },
            async getDocumentProjects() {
                try {
                    const query = `SELECT Name,
                                          Description
                                   FROM DocumentProject
                                   WHERE IsValid = 1`;
                    const res = await sqlQuery(http, { query });
                    this.documentProjects = res.data;
                } catch {
                    const errorMessage = 'Failed to fetch data for document projects';
                    this.showError(errorMessage);
                    this.errorMessage = errorMessage;
                }
            },
            async onClickShowDocumentProjects() {
                this.isOpenDocumentProjectModal = true;
                this.loadingDocumentProjects = true;
                await this.getDocumentProjects();
                this.loadingDocumentProjects = false;
            },
            onClose() {
                this.isOpenDocumentProjectModal = false;
            },
            getLibraryLink
        }
    };
</script>

<style scoped>
    .pos-rel{
        position: relative;
    }
</style>
